<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { Select } from 'primevue';
import { useLanguageStore } from 'shared/translations/stores/languageStore';
import { storeToRefs } from 'pinia';
import { useVehicleStore } from 'shared/vehicles/stores/vehicleStore';
import { useRefreshVehicleStore } from '@/vehicles/stores/refreshVehicleStore';
import DateRangeFilter from '../../shared/DateRangeFilter.vue';

interface DateRangeValues {
  from: string;
  to: string;
}

interface DateRangeFilterExposed {
  getFilterValues: () => DateRangeValues;
}

const languageStore = useLanguageStore();
const { translations } = storeToRefs(languageStore);

const dateRangeFilterRef = ref<DateRangeFilterExposed | null>(null);
const vehicleId = ref<string>('');

const vehicleStore = useVehicleStore();
const refreshVehicleStore = useRefreshVehicleStore();

const vehicles = computed(() => {
  return [...vehicleStore.vehicles]
    .sort((a, b) =>
      a.name.localeCompare(b.name, languageStore.language.code, {
        numeric: true,
        sensitivity: 'base',
      }),
    )
    .map((vehicle) => ({
      id: vehicle.id,
      name: vehicle.name,
    }));
});

onMounted(async () => {
  await refreshVehicleStore.initialize();
});

defineExpose({
  getFilterValues() {
    const dateRange = dateRangeFilterRef.value?.getFilterValues() ?? { from: '', to: '' };
    return {
      from: dateRange.from,
      to: dateRange.to,
      vehicleId: vehicleId.value || '',
    };
  },
});
</script>

<template>
  <div class="d-flex align-items-center gap-2 flex-nowrap">
    <DateRangeFilter ref="dateRangeFilterRef"></DateRangeFilter>

    <div class="inline-block">
      <span class="ms-3 me-1">{{ translations.generic.vehicle }}</span>
      <Select id="vehicle-select" v-model="vehicleId" :options="vehicles" optionLabel="name" optionValue="id"
        :placeholder="translations.generic.vehicle" showClear filter class="w-15rem"></Select>
    </div>
  </div>
</template>
